// ThemisRuleList.tsx
//
// Mirrors AdminDropdownConfig.tsx, but:
// - "Categories" are fixed phases (ThemisPhaseOptions)
// - Selecting a phase loads rules for that phase (GET /api/admin/themis/rules/by-phase?phase=...)
// - Add rule by name (POST /api/admin/themis/rules)
// - Drag drop reorder persists in one call (PUT /api/admin/themis/rules/reorder-by-phase)
// - Edit navigates to /rules/:id

import { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";

import { useAlert } from "../../../components/Alert/Alerts";
import Button from "../../../components/Button/Button";
import { CardBody } from "../../../components/DashboardCard/DashboardCard";
import TextField from "../../../components/FormFields/TextField";
import { FocusBorder } from "../../../components/FormFields/FieldTemplate";
import { SelectBox } from "../Judging/JudgingConfig/EntrySetTab";

import {
  createThemisRule,
  deleteThemisRule,
  getThemisRulesByPhase,
  reorderThemisRulesByPhase,
} from "./manageThemisRules";
import DraggableThemisRulesList from "./DraggableThemisRulesList";
import ConfirmModal from "./ConfirmModal";

export enum ThemisPhase {
  ResolveBasePrice = "pricing.resolve_base",
  ApplyLineDiscounts = "pricing.line_discounts",
  ApplyLineFees = "pricing.line_fees",
  ApplyLineTaxes = "pricing.line_taxes",
  ApplyTargetDiscounts = "pricing.target_discounts",
  ApplyTargetFees = "pricing.target_fees",
  ApplyTargetTaxes = "pricing.target_taxes",
  FinalizeTotals = "pricing.finalize",
  Invoicing = "pricing.invoicing",
}

export type DropdownOptions = { value: string; label: string };

export const ThemisPhaseOptions: DropdownOptions[] = [
  { value: ThemisPhase.ResolveBasePrice, label: "Resolve Base Price" },
  { value: ThemisPhase.ApplyLineDiscounts, label: "Line Discounts" },
  { value: ThemisPhase.ApplyLineFees, label: "Line Fees" },
  { value: ThemisPhase.ApplyLineTaxes, label: "Line Taxes" },
  { value: ThemisPhase.ApplyTargetDiscounts, label: "Target Discounts" },
  { value: ThemisPhase.ApplyTargetFees, label: "Target Fees" },
  { value: ThemisPhase.ApplyTargetTaxes, label: "Target Taxes" },
  { value: ThemisPhase.FinalizeTotals, label: "Finalize Totals" },
  { value: ThemisPhase.Invoicing, label: "Invoicing" },
];

export interface ThemisRuleListItem {
  id: number;
  name: string;
  targetScopeKey: string;
  phase: string;
  priority: number;
  isEnabled: boolean;
  stopProcessing: boolean;
}

export const OptionsSection = styled.div`
  display: flex;
  width: 50%;
  align-self: stretch;
  flex: 1;
  gap: 3rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colorBorderLight};
  max-width: 50%;
  min-height: 400px;
`;

export const StyledCategoryCard = styled.div<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colorBackgroundLight};
  padding: 0.5rem;
  padding-left: calc(0.5rem + 2px);
  border: solid 1px ${({ theme }) => theme.colorBorderLight};
  cursor: pointer;

  ${FocusBorder} {
    left: 0;
  }

  ${(p) =>
    !p.isSelected &&
    css`
      &:hover {
        & > ${FocusBorder} {
          width: 4px;
          background: ${({ theme }) => theme.colorActivation};
        }
      }
    `};

  ${(p) =>
    p.isSelected &&
    css`
      background: ${({ theme }) => theme.colorSelected};
    `};

  .category-display {
    display: flex;
    align-items: center;
    min-height: 56px;
    margin-right: auto;
  }
`;

function isLinePhase(phase: ThemisPhase): boolean {
  return (
    phase === ThemisPhase.ResolveBasePrice ||
    phase === ThemisPhase.ApplyLineDiscounts ||
    phase === ThemisPhase.ApplyLineFees ||
    phase === ThemisPhase.ApplyLineTaxes
  );
}

function phaseLabel(phase: string | null): string {
  if (!phase) return "";
  return ThemisPhaseOptions.find((p) => p.value === phase)?.label ?? phase;
}

const ThemisRuleList = () => {
  const [selectedPhase, setSelectedPhase] = useState<ThemisPhase | null>(null);
  const [rules, setRules] = useState<ThemisRuleListItem[]>([]);
  const [newRuleName, setNewRuleName] = useState("");
  const [rulePendingDelete, setRulePendingDelete] =
    useState<ThemisRuleListItem | null>(null);

  const { addNewAlert } = useAlert();
  const history = useHistory();

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {},
  });

  const selectedPhaseLabel = useMemo(
    () => phaseLabel(selectedPhase),
    [selectedPhase],
  );

  useEffect(() => {
    if (!selectedPhase) return;

    console.log("[ThemisRuleList] selectedPhase:", selectedPhase);

    getThemisRulesByPhase(selectedPhase)
      .then((res) => {
        console.log("[ThemisRuleList] GET /rules/by-phase status:", res.status);
        console.log("[ThemisRuleList] raw res.data:", res.data);

        const first: any = Array.isArray(res.data) ? res.data[0] : null;
        console.log("[ThemisRuleList] first item:", first);
        console.log(
          "[ThemisRuleList] first item keys:",
          first ? Object.keys(first) : null,
        );

        if (res.status === 200) setRules((res.data as any) ?? []);
        else setRules([]);
      })
      .catch((err) => {
        console.log("[ThemisRuleList] load rules error:", err);
        console.log("[ThemisRuleList] status:", err?.response?.status);
        console.log("[ThemisRuleList] data:", err?.response?.data);
        console.log("[ThemisRuleList] headers:", err?.response?.headers);
        setRules([]);
        addNewAlert({ type: "error", message: "Failed to load rules." });
      });
  }, [selectedPhase]);

  useEffect(() => {
    console.log("[ThemisRuleList] rules state length:", rules.length);
    console.log("[ThemisRuleList] rules state sample:", rules[0]);
  }, [rules]);

  useEffect(() => {
    if (!selectedPhase) return;

    getThemisRulesByPhase(selectedPhase)
      .then((res) => {
        if (res.status === 200) setRules(res.data ?? []);
        else setRules([]);
      })
      .catch(() => {
        setRules([]);
        addNewAlert({ type: "error", message: "Failed to load rules." });
      });
  }, [selectedPhase]);

  const handleAddRule = () => {
    const trimmed = newRuleName.trim();
    if (!trimmed || !selectedPhase) return;

    if (rules.some((r) => r.name.toLowerCase() === trimmed.toLowerCase())) {
      addNewAlert({
        type: "error",
        message: "Rule already exists in this phase.",
      });
      return;
    }

    const targetScopeKey = isLinePhase(selectedPhase)
      ? "OrderLineItem"
      : "Order";

    // Priority should be the order in this phase list.
    const priority = rules.length + 1;

    createThemisRule({
      name: trimmed,
      description: "",
      targetScopeKey,
      phase: selectedPhase,
      priority,
      isEnabled: true,
      stopProcessing: false,
    })
      .then((res) => {
        if (res.status === 200) {
          setNewRuleName("");
          addNewAlert({ type: "success", message: "Rule created." });

          // Create returns IdResponseDTO, so refetch phase list to get list item shape
          return getThemisRulesByPhase(selectedPhase);
        }
        throw new Error("Create failed");
      })
      .then((res) => {
        if (res?.status === 200) setRules(res.data ?? []);
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to create rule." });
      });
  };

  const confirmDeleteRule = (rule: ThemisRuleListItem) => {
    setRulePendingDelete(rule);
  };

  const handleConfirmDelete = () => {
    if (!rulePendingDelete) return;

    const id = rulePendingDelete.id;

    deleteThemisRule(id)
      .then(() => {
        setRules((prev) => prev.filter((r) => r.id !== id));
        addNewAlert({ type: "success", message: "Rule deleted." });
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to delete rule." });
      })
      .finally(() => {
        setRulePendingDelete(null);
      });
  };

  const handleEditRule = (id: number) => {
    history.push(`/themis/rules/rule/${id}`);
  };

  const onReorder = (orderedIds: number[]) => {
    if (!selectedPhase) return;

    reorderThemisRulesByPhase(selectedPhase, orderedIds)
      .then(() => {
        setRules((prev) => {
          const byId = new Map(prev.map((r) => [r.id, r]));
          const reordered = orderedIds
            .map((id) => byId.get(id))
            .filter(Boolean) as ThemisRuleListItem[];

          // Keep any stragglers just in case (should not happen)
          const used = new Set(orderedIds);
          const rest = prev.filter((r) => !used.has(r.id));
          return [...reordered, ...rest];
        });

        addNewAlert({ type: "success", message: "Order updated." });
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to update order." });
      });
  };

  // Map rules into the shape your existing draggable list expects
  const draggableItems = useMemo(
    () =>
      rules
        .slice()
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
        .map((r, idx) => ({
          id: r.id,
          category: selectedPhase ?? "",
          name: r.name,
          order: idx,
        })),
    [rules, selectedPhase],
  );

  return (
    <FormikProvider value={formik}>
      <CardBody className="flex gap-[2rem]">
        {/* Phase list */}
        <div className="w-[400px]">
          <h2 className="mb-[1rem] mt-[1.5rem]">Phases</h2>

          <ul className="flex flex-col gap-[.5rem] mb-[1rem]">
            {ThemisPhaseOptions.map((phase) => (
              <StyledCategoryCard
                key={phase.value}
                isSelected={selectedPhase === phase.value}
                onClick={() => setSelectedPhase(phase.value as ThemisPhase)}
              >
                <FocusBorder />
                <p className="category-display">{phase.label}</p>
              </StyledCategoryCard>
            ))}
          </ul>
        </div>

        {/* Rules list */}
        <OptionsSection>
          <div className="flex-1">
            <h2 className="mb-[.25rem]">Rules</h2>
            {selectedPhase ? (
              <p className="mb-[1rem] text-sm opacity-70">
                Phase: {selectedPhaseLabel}
              </p>
            ) : null}

            {selectedPhase ? (
              <>
                <div className="flex gap-[.5rem] mb-[1rem]">
                  <TextField
                    name="newRuleName"
                    className="w-[300px]"
                    placeholder="New Rule Name"
                    value={newRuleName}
                    onChange={(e) => setNewRuleName(e.target.value)}
                  />
                  <Button icon="plus" onClick={handleAddRule}>
                    Add
                  </Button>
                </div>
                <DraggableThemisRulesList
                  rules={rules}
                  onEdit={(id) => history.push(`/themis/rules/rule/${id}`)}
                  onDelete={(id) => {
                    const rule = rules.find((r) => r.id === id);
                    if (rule) confirmDeleteRule(rule);
                  }}
                  onReorder={(orderedIds) =>
                    reorderThemisRulesByPhase(selectedPhase!, orderedIds).then(
                      () => {},
                    )
                  }
                />
              </>
            ) : (
              <SelectBox>Please select a phase</SelectBox>
            )}
          </div>
        </OptionsSection>
      </CardBody>
      <ConfirmModal
        isOpen={!!rulePendingDelete}
        title="Delete rule?"
        message={
          rulePendingDelete
            ? `Are you sure you want to delete "${rulePendingDelete.name}"? This cannot be undone.`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setRulePendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </FormikProvider>
  );
};

export default ThemisRuleList;
